<script setup lang="ts">
import { type HTMLAttributes, useSlots } from 'vue';
import { cn } from '@ui/lib/utils.js';
import { reactiveOmit } from '@vueuse/core';
import { Separator, type SeparatorProps } from 'reka-ui';

const props = withDefaults(defineProps<SeparatorProps & { class?: HTMLAttributes['class'] }>(), {
    orientation: 'horizontal',
    decorative: true,
});

const delegatedProps = reactiveOmit(props, 'class');
const slots = useSlots();
</script>

<template>
    <Separator
        v-if="!slots.default?.()"
        data-slot="separator-root"
        v-bind="delegatedProps"
        :class="
            cn(
                'bg-border-default',
                'shrink-0',
                'data-[orientation=horizontal]:h-px',
                'data-[orientation=horizontal]:w-full',
                'data-[orientation=vertical]:w-px',
                'data-[orientation=vertical]:h-unset',
                'data-[orientation=vertical]:self-stretch',
                props.class,
            )
        "
    />
    <div
        v-else
        data-slot="separator-root"
        :data-orientation="orientation"
        :class="
            cn(
                'flex',
                'items-center',
                'gap-4',
                'data-[orientation=horizontal]:w-full',
                'data-[orientation=horizontal]:flex-row',
                'data-[orientation=vertical]:flex-col',
                'data-[orientation=vertical]:h-unset',
                'data-[orientation=vertical]:self-stretch',
                props.class,
            )
        "
    >
        <Separator
            data-slot="separator-line"
            v-bind="delegatedProps"
            :class="cn(`bg-border-default grow data-[orientation=horizontal]:h-px data-[orientation=vertical]:w-px`)"
        />
        <slot />
        <Separator
            data-slot="separator-line"
            v-bind="delegatedProps"
            :class="cn(`bg-border-default grow data-[orientation=horizontal]:h-px data-[orientation=vertical]:w-px`)"
        />
    </div>
</template>
